import clsx from 'clsx';
import * as React from 'react';
import { Button } from 'react-aria-components';

import type { SVGIconComponent } from '@gitbook/spine-icons';

import { ButtonLabel } from '../Button';

export const getPlaceholderStyles = (props: { isFocusVisible: boolean; isHovered: boolean }) =>
    clsx(
        'border border-dashed',
        props.isFocusVisible || props.isHovered
            ? 'border-[var(--neutral-border-interactive)] bg-[var(--neutral-ui-bg-hover)] text-[var(--neutral-text-high)]'
            : 'border-[var(--neutral-border-subtle)] bg-[var(--neutral-surface)] text-[var(--neutral-text-low)]',
        props.isFocusVisible && 'focus-ring'
    );

export function PlaceholderButton({
    ref,
    label,
    size = 'medium',
    onPress,
    icon: Icon,
    className,
    kind = 'default',
    disabled,
    iconOnly,
}: {
    label: string;
    icon?: SVGIconComponent;
    size?: 'large' | 'medium' | 'small';
    onPress?: () => void;
    onDrop?: (event: React.DragEvent<HTMLButtonElement>) => void;
    className?: string;
    kind?: 'default' | 'pill' | 'ghost';
    disabled?: boolean;
    iconOnly?: boolean;
} & { ref?: React.Ref<HTMLButtonElement> }) {
    return (
        <Button
            ref={ref}
            aria-label={label}
            onPress={onPress}
            isDisabled={disabled}
            className={(props) =>
                clsx(
                    className,
                    'flex cursor-pointer items-center gap-2 overflow-hidden rounded-sm text-ellipsis whitespace-nowrap transition-all animate-in fade-in',
                    !iconOnly && 'flex-1',
                    'cursor-pointer text-nowrap',
                    getPlaceholderStyles(props),
                    kind === 'ghost' ? 'absolute' : 'relative',
                    {
                        'justify-center text-ui-action': kind === 'default' || kind === 'ghost',
                    },
                    {
                        'shrink grow-0 basis-auto rounded-full px-1 py-1 text-ui-small':
                            kind === 'pill',
                    },
                    !iconOnly && {
                        'p-2': size === 'small',
                        'm-1 p-2': size === 'medium',
                        'mx-1 mt-1 p-14': size === 'large',
                    },
                    iconOnly && {
                        'size-6': size === 'small',
                        'size-8': size === 'medium',
                        'size-9': size === 'large',
                    },
                    iconOnly && 'justify-center',
                    {
                        'mx-0': kind === 'ghost',
                    }
                )
            }
            data-palette-anchor=""
        >
            {Icon && <Icon className="size-4 shrink-0" />}
            {label && !iconOnly ? (
                <ButtonLabel hasIcon={Boolean(Icon)} iconOnly={iconOnly} size={size}>
                    {label}
                </ButtonLabel>
            ) : null}
        </Button>
    );
}
